// src/components/inbox/SummaryTab.jsx
import React, { useId, useState } from "react";
import { useDispatch } from "react-redux";
import { Badge, Button, Textarea } from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { useSnackbar } from "notistack";
import { saveClientNote } from "../../shared/_services/inbox.services";
import { noteSaved } from "../../redux/inboxSlice";
import {
  accountsLabel,
  answeredAt,
  apptTime,
  calendarDate,
  clientNoteLine,
  instantDate,
  meetingsSub,
  meetingsValue,
  noClientNoteYet,
  noteSavedLine,
  planSub,
  SAVE_FAILED,
  shortName,
  withYou,
} from "./inboxFormat";

const INTERNAL = "Internal · the client never sees this";
const NOTE_MAX = 8000;

function Fact({ label, value, sub }) {
  return (
    <div data-slot="inbox-fact" className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-2">
      <span className="tw-text-xs tw-text-text-muted">{label}</span>
      <span className="tw-text-sm tw-font-bold tw-text-text">{value}</span>
      {sub && <span className="tw-text-xs tw-text-text-muted">{sub}</span>}
    </div>
  );
}

// The fenced internal note: one running note per client (SP3 owner decision
// 4), kept with history on the server. Plain text in, plain text out: it is
// always rendered as a text node (Review Focus #4).
function NoteBlock({ clientId, note, first }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const eyebrowId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const hasText = !!note.text;
  // Edit (or Add a note) toggles the editor, as the comp does: pressed
  // again it closes the editor and the draft is discarded (M7).
  const toggleEditing = () => {
    if (!editing) setDraft(note.text || "");
    setEditing(!editing);
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await saveClientNote(clientId, draft);
      dispatch(noteSaved({ clientId, note: res.data.note }));
      setEditing(false);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || SAVE_FAILED, {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-note-border tw-bg-note-bg tw-p-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span
          id={eyebrowId}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-bold tw-uppercase tw-text-warning"
        >
          <StarsIcon name="lock" className="tw-h-4 tw-w-4 tw-shrink-0" />
          {INTERNAL}
        </span>
        {note.canEdit && (
          <Button variant="text" size="sm" onClick={toggleEditing}>
            {hasText ? "Edit" : "Add a note"}
          </Button>
        )}
      </div>
      <p className="tw-m-0 tw-text-xs tw-text-text-muted">{clientNoteLine(first)}</p>
      {editing ? (
        <>
          <Textarea
            rows={4}
            aria-labelledby={eyebrowId}
            maxLength={NOTE_MAX}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button
            variant="quiet"
            size="sm"
            className="tw-self-start"
            onClick={save}
            disabled={saving}
          >
            Save note
          </Button>
        </>
      ) : hasText ? (
        <>
          <p className="tw-m-0 tw-whitespace-pre-wrap tw-text-sm">{note.text}</p>
          {note.updatedAt && note.author && (
            <p className="tw-m-0 tw-text-xs tw-text-text-muted">
              {noteSavedLine(note.updatedAt, note.author.firstName)}
            </p>
          )}
        </>
      ) : (
        <p className="tw-m-0 tw-text-xs tw-text-text-muted">{noClientNoteYet(first)}</p>
      )}
    </div>
  );
}

export default function SummaryTab({ clientId, summary, viewerId }) {
  const {
    client,
    note,
    lastReply,
    plan,
    meetings,
    appointments,
    goalThemes = [],
    institutions = [],
    recentUploads = [],
    bookedWith,
  } = summary;
  const appts = [appointments?.next, appointments?.last].filter(Boolean);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
      <div className="tw-flex tw-flex-col tw-gap-1">
        <h3 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">{`Summary · ${shortName(client)}`}</h3>
        <p className="tw-m-0 tw-text-xs tw-text-text-muted">
          Facts from the record. Nothing here is written by a model.
        </p>
      </div>

      <NoteBlock
        clientId={clientId}
        note={note ?? { text: null, canEdit: false }}
        first={client?.firstName ?? ""}
      />

      {lastReply ? (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface-sunken tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Last reply from us</span>
            <span className="tw-m-0 tw-text-xs tw-text-text-muted">
              {`${withYou(lastReply.by, viewerId)} · ${answeredAt(lastReply.at)}`}
            </span>
          </div>
          <p className="tw-m-0 tw-whitespace-pre-wrap tw-text-sm">{lastReply.text}</p>
        </div>
      ) : (
        <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">No reply from us yet.</p>
      )}

      <div className="tw-grid tw-grid-cols-3 tw-gap-2">
        <Fact label="Plan" value={plan?.name || "—"} sub={planSub(plan)} />
        <Fact
          label="Meetings"
          value={meetingsValue(meetings)}
          sub={meetingsSub(meetings)}
        />
        <Fact
          label="Booked with"
          value={bookedWith ? bookedWith.firstName : "Nobody yet"}
          sub={
            bookedWith
              ? `${bookedWith.when} ${calendarDate(bookedWith.date)}`
              : "no meeting booked"
          }
        />
      </div>

      <div className="tw-grid tw-gap-2">
        <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Meetings</div>
        {appts.length > 0 ? (
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {appts.map((a) => (
              <li key={a._id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                <span>{`${calendarDate(a.date)} · ${apptTime(a.time)} · ${a.advisor?.firstName ?? ""}`}</span>
                <Badge>{a.status}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">No meetings yet.</p>
        )}
      </div>

      <div className="tw-grid tw-gap-2">
        <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Linked</div>
        {institutions.length > 0 ? (
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {institutions.map((inst, i) => (
              <li key={`${inst.name}-${i}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                <span>{inst.name}</span>
                <span className="tw-m-0 tw-text-xs tw-text-text-muted">
                  {accountsLabel(inst.accountCount)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">
            No accounts linked. The client links them under Money.
          </p>
        )}
      </div>

      <div className="tw-grid tw-gap-2">
        <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Goal themes, generated from intake</div>
        {goalThemes.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {goalThemes.map((g) => (
              <Badge key={g}>{g}</Badge>
            ))}
          </div>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">
            Onboarding not finished; no themes yet.
          </p>
        )}
      </div>

      <div className="tw-grid tw-gap-2">
        <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Recent uploads</div>
        {recentUploads.length > 0 ? (
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {recentUploads.map((u) => (
              <li key={u.documentId} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 tw-text-sm last:tw-border-b-0">
                <span>{u.filename}</span>
                <span className="tw-m-0 tw-text-xs tw-text-text-muted">{instantDate(u.uploadedAt)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-divider tw-px-3 tw-py-2 tw-text-sm tw-text-text-muted">Nothing uploaded yet.</p>
        )}
      </div>
    </div>
  );
}
